import type { Participant } from '@seen/core/models/patients/participant'
import {
  Badge,
  ContextMenu,
  ContextMenuContent,
  ContextMenuTrigger,
} from '@seen/ui'
import type { ReactNode } from 'react'
import { ContextMenuMedplumLinkItem } from '#src/components/ContextMenuMedplumLinkItem.tsx'
import { ContextMenuOpenInNewTab } from '#src/components/ContextMenuOpenInNewTab.tsx'
import { Avatar } from '#src/components/core/Avatar/Avatar.tsx'
import { Link } from '#src/components/core/Link.tsx'
import { PatientName } from '#src/components/patient/PatientName.tsx'
import { BirthDateBadge } from '#src/pages/participants/participant-badges/BirthDateBadge.tsx'
import { EnrollmentDateBadge } from '#src/pages/participants/participant-badges/EnrollmentDateBadge.tsx'
import { MRNBadge } from '#src/pages/participants/participant-badges/MRNBadge.tsx'
import { ParticipantLabelsBadge } from '#src/pages/participants/participant-badges/ParticipantLabelsBadge.tsx'
import { PhoneBadge } from '#src/pages/participants/participant-badges/PhoneBadge.tsx'
import type { ParticipantViewDisplay } from '#src/pages/participants/useParticipantsPageStore.ts'

export function ParticipantRow({
  participant,
  display,
}: {
  participant: Participant
  display: ParticipantViewDisplay
}): ReactNode {
  return (
    <ContextMenu>
      <ContextMenuTrigger asChild>
        <Link
          to={`/Patient/${participant.id}`}
          data-testid="participant-row"
          className="flex items-center gap-2 px-pagex-default py-2 hover:bg-accent data-[state=open]:bg-accent lg:px-pagex-lg"
        >
          <div className="flex items-center gap-2.5 py-0.5">
            <Avatar profile={participant.reference} size={6} />
            <PatientName
              participant={participant}
              className="text-sm font-medium"
            />
            {participant.isLead && <Badge variant="outline">Lead</Badge>}
            {participant.isDisenrolled && (
              <Badge variant="outline" color="red">
                Disenrolled
              </Badge>
            )}
          </div>
          <div className="flex max-h-[1.625rem] flex-1 flex-wrap items-center gap-1 overflow-hidden">
            {participant.birthDateFormatted && display.showBirthDate && (
              <BirthDateBadge birthDate={participant.birthDateFormatted} />
            )}
            {participant.primaryPhoneNumber && display.showPhoneNumber && (
              <PhoneBadge phoneNumber={participant.primaryPhoneNumber} />
            )}
            {participant.seenMRN && display.showMRN && (
              <MRNBadge mrn={participant.seenMRN} />
            )}
            {participant.enrollmentDateString && display.showEnrollmentDate && (
              <EnrollmentDateBadge
                enrollmentDate={participant.enrollmentDateString}
              />
            )}
            {display.showLabels && (
              <ParticipantLabelsBadge participant={participant} />
            )}
          </div>
        </Link>
      </ContextMenuTrigger>
      <ContextMenuContent>
        <ContextMenuOpenInNewTab href={`/Patient/${participant.id}`} />
        <ContextMenuMedplumLinkItem to={participant.reference} />
      </ContextMenuContent>
    </ContextMenu>
  )
}
